import _ from "lodash";
import React from "react";
import PropTypes from "prop-types";
import classes from "./DateSelector.scss";
import moment from "moment";
import classNames from "classnames";
import { CalendarDateOutlined } from "@toddle-edu/ds-icons";
import "react-dates/initialize";
import { SingleDatePicker } from "react-dates";
import "react-dates/lib/css/_datepicker.css";
import { isDayOutSideRange, scrollIntoView } from "Utils";

import UIBaseComponent from "UIComponents/UIBaseComponent";
import I18nHOC from "UIComponents/I18nHOC";
import {
  ICON_AFTER_POSITION,
  ICON_BEFORE_POSITION,
} from "react-dates/constants";

class DateSelector extends UIBaseComponent {
  constructor(props) {
    super(props);
    this.state = { error: "", focused: false };
  }

  isValidValue = value => {
    return _.get(_.keys(value), "length", 0) != 0;
  };

  updateInputField = (value, name) => {
    const {
      isValueMomentObj,
      value: currentDate,
      checkForSameDate,
    } = this.props;
    if (value) {
      const param = {};
      param[name] = isValueMomentObj
        ? value
        : moment(value).format("YYYY-MM-DD");

      const isSameDate = moment(currentDate).isSame(param[name]);

      if (isSameDate && checkForSameDate) {
        param[name] = null;
        this.props.updateInputField(param);
      } else {
        this.props.updateInputField(param);
      }

      if (this.state.error != "") {
        this.setState({ error: "" });
      }
    }

    this.onClickDatePicker();
  };

  onClickDatePicker = e => {
    e && e.preventDefault();
  };

  isOutsideRange = day => {
    const { maxDate, minDate } = this.props;
    return isDayOutSideRange({ day, maxDate, minDate });
  };

  setFocused = bool => {
    this.setState({ focused: bool });
  };

  onFocusChangeLocal = ({ focused }) => {
    const {
      value,
      displayFormat,
      onFocusChange,
      isScrollIntoViewRequired = false,
      updateInputField,
      name,
    } = this.props;

    // const isDateValid = moment(value, displayFormat).isValid();

    // if (!_.isEmpty(value) && !isDateValid && !this.state.focused) {
    //   // when date is invalid set selected date to current date
    //   updateInputField({ [`${name}`]: moment() });
    //   // open date picker
    //   this.setState({ focused });
    //   return;
    // }

    onFocusChange ? onFocusChange(focused) : null;
    this.setState({ focused });

    if (focused) {
      this.onFocus();

      isScrollIntoViewRequired &&
        setTimeout(() => {
          const singleDatePickerDomNode = document.getElementsByClassName(
            "DayPicker_transitionContainer"
          )[0];

          scrollIntoView({
            element: singleDatePickerDomNode,
            block: "center",
          });
        }, 300);
    } else {
      this.onBlur();
    }
  };

  returnYears = () => {
    const years = [];
    for (let i = moment().year() - 50; i <= moment().year() + 50; i++) {
      years.push(<option value={i}>{i}</option>);
    }
    return years;
  };

  renderMonthElement = ({ month, onMonthSelect, onYearSelect }) => (
    <div className={classes.monthYearSelectorcontainer}>
      <div className={classes.monthDropDownContainer}>
        <select
          value={month.month()}
          onChange={e => onMonthSelect(month, e.target.value)}
        >
          {_.map(moment.months(), (label, value) => (
            <option key={value} value={value}>
              {label}
            </option>
          ))}
        </select>
      </div>

      <div className={classes.yearDropDownContainer}>
        <select
          value={month.year()}
          onChange={e => onYearSelect(month, e.target.value)}
        >
          {this.returnYears()}
        </select>
      </div>
    </div>
  );

  renderEdit = () => {
    const {
      value,
      name,
      isDisabled,
      openDirection,
      numberOfMonths,
      placeholder,
      placeholder_locale,
      dateClassname,
      t,
      inputIconPosition,
      displayFormat,
      dateSelectorContainerStyle,
      isBottomPaddingRequired,
      inputIconSize,
      inputIconVariant,
      anchorDirection,
      showMonthAndYearSelector,
      testId,
    } = this.props;

    const selectClassName = classNames(
      { [classes.container]: true },
      { [classes.disabledContainer]: isDisabled },
      { [classes.errorContainer]: Boolean(this.state.error) },
      {
        [classes.downContainer]:
          openDirection === "down" && isBottomPaddingRequired,
      },
      dateClassname
    );

    const { focused } = this.state;

    return (
      <div
        className={selectClassName}
        style={dateSelectorContainerStyle}
        data-test-id={testId}
      >
        <SingleDatePicker
          id="date_input"
          isOutsideRange={this.isOutsideRange}
          anchorDirection={anchorDirection}
          date={value && moment(value).isValid() ? moment(value) : null}
          focused={focused}
          onDateChange={props => this.updateInputField(props, name)}
          openDirection={openDirection}
          disabled={isDisabled}
          showDefaultInputIcon={true}
          customInputIcon={
            <CalendarDateOutlined
              size={inputIconSize}
              variant={inputIconVariant}
            />
          }
          hideKeyboardShortcutsPanel={true}
          onFocusChange={this.onFocusChangeLocal}
          numberOfMonths={numberOfMonths}
          placeholder={
            placeholder === "common:select_with_label"
              ? t(placeholder, { label: t(placeholder_locale) })
              : placeholder
          }
          inputIconPosition={
            inputIconPosition === "before"
              ? ICON_BEFORE_POSITION
              : ICON_AFTER_POSITION
          }
          displayFormat={displayFormat}
          renderMonthElement={
            showMonthAndYearSelector ? this.renderMonthElement : null
          }
        />
      </div>
    );
  };
}

DateSelector.defaultProps = {
  ...UIBaseComponent.defaultProps,
  minDate: null,
  maxDate: null,
  isDisabled: false,
  autoFocus: false,
  openDirection: "down",
  anchorDirection: "left",
  isValueMomentObj: true,
  numberOfMonths: 1,
  placeholder: "common:select_with_label",
  placeholder_locale: "common:date",
  inputIconPosition: "before",
  inputIconSize: "xx-small",
  inputIconVariant: "default",
  displayFormat: "DD MMM YYYY",
  isBottomPaddingRequired: true,
  showMonthAndYearSelector: false,
  checkForSameDate: false,
  testId: undefined,
};

DateSelector.propTypes = {
  ...UIBaseComponent.propTypes,
  isDisabled: PropTypes.bool,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  dateClassname: PropTypes.string,
  datePickerClassName: PropTypes.string,
  placeholder: PropTypes.string,
  minDate: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  updateInputField: PropTypes.func,
  name: PropTypes.string,
  openDirection: PropTypes.oneOf(["up", "down"]),
  anchorDirection: PropTypes.oneOf(["left", "right"]),
  numberOfMonths: PropTypes.number,
  placeholder_locale: PropTypes.string,
  inputIconPosition: PropTypes.oneOf(["before", "after"]),
  isBottomPaddingRequired: PropTypes.bool,
  showMonthAndYearSelector: PropTypes.bool,
  checkForSameDate: PropTypes.bool,
  testId: PropTypes.string,
};

export default I18nHOC({ resource: ["common"] })(DateSelector);
